export const BRAND = {
  orange: "#EE9322",
  blue: "#4C72B8",
  blueLight: "#7BA0D4",
  green: "#6CBF67",
  red: "#D93025",
} as const;

/**
 * Readable TEXT variants of the four BRAND hues, for text rendered on a
 * same-hue tinted pill (activity tiers, daily-activity tiers). Green and
 * orange from BRAND are ~2.3:1 on a white card; blue and red are ~3.0:1 on a
 * dark card — both fail WCAG AA (4.5:1). These resolve the matching
 * `--pill-text-*` CSS variable (see index.css), so they follow the active
 * theme automatically without needing to know it in JS.
 */
export const PILL_TEXT = {
  green: "hsl(var(--pill-text-green))",
  blue: "hsl(var(--pill-text-blue))",
  orange: "hsl(var(--pill-text-orange))",
  red: "hsl(var(--pill-text-red))",
} as const;

export function hexToRgb(hex: string): [number, number, number] {
  const normalized = hex.replace("#", "");
  return [
    parseInt(normalized.slice(0, 2), 16),
    parseInt(normalized.slice(2, 4), 16),
    parseInt(normalized.slice(4, 6), 16),
  ];
}

function rgbToHex([r, g, b]: [number, number, number]): string {
  return `#${[r, g, b]
    .map((c) => Math.round(Math.max(0, Math.min(255, c))).toString(16).padStart(2, "0"))
    .join("")}`;
}

function linearize(channel8Bit: number): number {
  const s = channel8Bit / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

function relativeLuminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex);
  return 0.2126 * linearize(r) + 0.7152 * linearize(g) + 0.0722 * linearize(b);
}

function contrastRatio(luminanceA: number, luminanceB: number): number {
  const lighter = Math.max(luminanceA, luminanceB);
  const darker = Math.min(luminanceA, luminanceB);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Picks black or white text for an arbitrary background hex, whichever has
 * higher WCAG contrast. For a fixed 4-colour ramp (BRAND) a hand-picked text
 * colour per hue is fine (see PILL_TEXT above) — but the industry palette
 * (industry-colors.ts) has 12 hand-picked hues spanning near-white to
 * near-black, where a single fixed text colour reads fine on some and fails
 * contrast on others (UI-12).
 */
export function readableTextColor(backgroundHex: string): "#FFFFFF" | "#141414" {
  const bgLuminance = relativeLuminance(backgroundHex);
  const contrastWithWhite = contrastRatio(bgLuminance, 1);
  const contrastWithBlack = contrastRatio(bgLuminance, 0);
  return contrastWithWhite >= contrastWithBlack ? "#FFFFFF" : "#141414";
}

/** A translucent fill for a same-hue "tinted pill" (see readablePillText). */
export function pillTint(hex: string, alpha: number): string {
  const [r, g, b] = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const AA_CONTRAST_MIN = 4.5;
// Close to the app's actual card background extremes (near-white light
// card, near-black dark card) -- not exactly 0/1, since nothing here is
// pure black or white, but close enough that the contrast check lands the
// same verdict a measurement against the real card colour would.
const CARD_LUMINANCE = { light: 0.97, dark: 0.02 } as const;

/**
 * A same-hue text colour for a translucent pill, darkened (light card) or
 * lightened (dark card) just enough to clear WCAG AA -- readableTextColor's
 * binary black/white is right for a SOLID pill, but a translucent one reads
 * as "generic label" rather than "this industry" once the colour is gone
 * entirely. Steps toward black/white in increments and stops at the first
 * one that passes, so a colour that's already readable keeps most of its
 * hue while a very pale or very dark one (same failure mode readableTextColor
 * exists for -- see UI-12) still ends up legible.
 */
export function readablePillText(hex: string, cardIsDark: boolean): string {
  const cardLuminance = cardIsDark ? CARD_LUMINANCE.dark : CARD_LUMINANCE.light;
  const target: [number, number, number] = cardIsDark ? [255, 255, 255] : [0, 0, 0];
  const [r, g, b] = hexToRgb(hex);
  for (let amount = 0; amount <= 1; amount += 0.1) {
    const candidate: [number, number, number] = [
      r + (target[0] - r) * amount,
      g + (target[1] - g) * amount,
      b + (target[2] - b) * amount,
    ];
    if (contrastRatio(relativeLuminance(rgbToHex(candidate)), cardLuminance) >= AA_CONTRAST_MIN) {
      return rgbToHex(candidate);
    }
  }
  return rgbToHex(target);
}
